import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../lib/authFetch';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription,
  Button, Badge
} from '../components/ui';
import { formatNumber, formatDate } from '../lib/utils';
import {
  ArrowLeft, RefreshCw, TrendingUp, TrendingDown, Users,
  MousePointerClick, Globe, Smartphone, Monitor, Tablet,
  BarChart3, PieChart
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, BarChart, Bar
} from 'recharts';

const COLORS = ['#10B981', '#6366F1', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function TestAnalytics() {
  const { testId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [test, setTest] = useState(null);
  const [dateRange, setDateRange] = useState('7d');

  const loadData = useCallback(async () => {
    if (!testId) return;
    setLoading(true);

    try {
      const res = await authFetch(`/api/ab?resource=tests&testId=${testId}`);
      const data = await res.json();

      if (data.success) {
        setTest(data.test);
      } else {
        toast.error('Test not found');
      }
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [testId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!test) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <p>Test not found</p>
          <Link to="/ab-tests">
            <Button variant="outline" className="mt-4">Back to A/B Tests</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  // Process analytics data
  const variants = test.ab_test_variants || [];
  const visits = test.analytics?.visits || [];
  const conversions = test.analytics?.conversions || [];

  // Calculate stats
  const totalVisits = visits.length;
  const totalConversions = conversions.length;
  const overallCVR = totalVisits > 0 ? (totalConversions / totalVisits * 100) : 0;

  // Visits by day
  const visitsByDay = visits.reduce((acc, visit) => {
    const date = visit.visited_at?.split('T')[0];
    if (date) {
      acc[date] = (acc[date] || 0) + 1;
    }
    return acc;
  }, {});

  const chartData = Object.entries(visitsByDay)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-30)
    .map(([date, count]) => ({
      date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      visits: count,
    }));

  // Visits by country
  const visitsByCountry = visits.reduce((acc, visit) => {
    const country = visit.country_code || 'Unknown';
    acc[country] = (acc[country] || 0) + 1;
    return acc;
  }, {});

  const countryData = Object.entries(visitsByCountry)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([country, count]) => ({ name: country, value: count }));

  // Visits by device
  const visitsByDevice = visits.reduce((acc, visit) => {
    const device = visit.device_type || 'Unknown';
    acc[device] = (acc[device] || 0) + 1;
    return acc;
  }, {});

  const deviceData = Object.entries(visitsByDevice)
    .map(([device, count]) => ({ name: device, value: count }));

  // New vs returning visitors
  const returningVisits = visits.filter(v => v.is_returning === true);
  const newVisits       = visits.filter(v => v.is_returning !== true);
  const returningPct    = totalVisits > 0 ? (returningVisits.length / totalVisits * 100) : 0;

  // Top countries — expanded to 10
  const topCountries = Object.entries(visitsByCountry)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([country, count]) => ({ name: country, value: count }));

  // Variant performance
  const variantStats = variants.map(variant => {
    const variantVisits = variant.visits || 0;
    const variantConversions = variant.conversions || 0;
    const cvr = variantVisits > 0 ? (variantConversions / variantVisits * 100) : 0;
    
    return {
      ...variant,
      cvr,
      trafficShare: totalVisits > 0 ? (variantVisits / totalVisits * 100) : 0,
    };
  });

  // Find winner
  const winner = variantStats.reduce((best, current) => {
    if (!best || current.cvr > best.cvr) return current;
    return best;
  }, null);

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'secondary',
      running: 'success',
      paused: 'warning',
      completed: 'default',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/ab-tests/${testId}`}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Edit
          </Button>
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{test.name}</h1>
            {getStatusBadge(test.status)}
          </div>
          <p className="text-muted-foreground">
            Analytics & Performance
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg bg-card text-foreground"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Visits</p>
                <p className="text-2xl font-bold">{formatNumber(totalVisits)}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversions</p>
                <p className="text-2xl font-bold">{formatNumber(totalConversions)}</p>
              </div>
              <MousePointerClick className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
                <p className="text-2xl font-bold">{overallCVR.toFixed(2)}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Variants</p>
                <p className="text-2xl font-bold">{variants.length}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Variant Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Variant Performance</CardTitle>
          <CardDescription>
            Compare conversion rates across all variants
          </CardDescription>
        </CardHeader>
        <CardContent>
          {variantStats.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No variant data available yet
            </p>
          ) : (
            <div className="space-y-4">
              {variantStats.map((variant, idx) => (
                <div
                  key={variant.id}
                  className={`p-4 border rounded-lg ${
                    winner?.id === variant.id && totalVisits > 100
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      />
                      <span className="font-medium">{variant.name}</span>
                      {variant.is_control && (
                        <Badge variant="outline">Control</Badge>
                      )}
                      {winner?.id === variant.id && totalVisits > 100 && (
                        <Badge variant="success">Leading</Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold">{variant.cvr.toFixed(2)}%</span>
                      <span className="text-sm text-muted-foreground ml-1">CVR</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Visits:</span>
                      <span className="ml-2 font-medium">{formatNumber(variant.visits || 0)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Conversions:</span>
                      <span className="ml-2 font-medium">{formatNumber(variant.conversions || 0)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Traffic:</span>
                      <span className="ml-2 font-medium">{variant.trafficShare.toFixed(1)}%</span>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-3 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min(variant.cvr * 10, 100)}%`,
                        backgroundColor: COLORS[idx % COLORS.length],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Traffic Over Time */}
        <Card>
          <CardHeader>
            <CardTitle>Traffic Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No traffic data yet
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="visits"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Device Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Device Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {deviceData.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No device data yet
              </p>
            ) : (
              <div className="flex items-center gap-8">
                <ResponsiveContainer width={150} height={150}>
                  <RechartsPie>
                    <Pie
                      data={deviceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      dataKey="value"
                    >
                      {deviceData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {deviceData.map((item, idx) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      />
                      <span className="capitalize">{item.name}</span>
                      <span className="text-muted-foreground ml-auto">
                        {((item.value / totalVisits) * 100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New vs Returning + Top Countries */}
      <div className="grid gap-6 lg:grid-cols-2">

        {/* New vs Returning */}
        <Card>
          <CardHeader>
            <CardTitle>New vs Returning Visitors</CardTitle>
            <CardDescription>
              {test.returning_url
                ? `Returning visitors (within ${test.returning_window_days || 30} days) are redirected to a separate URL.`
                : 'Returning visitor redirect is not configured.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {totalVisits === 0 ? (
              <p className="text-center text-muted-foreground py-8">No visitor data yet</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-4">
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{formatNumber(newVisits.length)}</p>
                    <p className="text-sm text-muted-foreground mt-1">New Visitors</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">{(100 - returningPct).toFixed(1)}%</p>
                  </div>
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4">
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{formatNumber(returningVisits.length)}</p>
                    <p className="text-sm text-muted-foreground mt-1">Returning Visitors</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">{returningPct.toFixed(1)}%</p>
                  </div>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                  <div className="h-full bg-emerald-500 rounded-l-full transition-all"
                    style={{ width: `${100 - returningPct}%` }} />
                  <div className="h-full bg-amber-400 rounded-r-full transition-all"
                    style={{ width: `${returningPct}%` }} />
                </div>
                {test.returning_url && (
                  <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground flex items-center gap-2">
                    <span>↪</span>
                    <span>Returning visitors redirected to:</span>
                    <a href={test.returning_url} target="_blank" rel="noopener noreferrer"
                      className="text-primary hover:underline truncate">{test.returning_url}</a>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Countries */}
        <Card>
          <CardHeader>
            <CardTitle>Top Countries</CardTitle>
          </CardHeader>
          <CardContent>
            {topCountries.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No geographic data yet</p>
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto">
                {topCountries.map((country) => (
                  <div key={country.name} className="flex items-center gap-3">
                    <span className="w-8 text-center text-lg flex-shrink-0">
                      {getCountryFlag(country.name)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between mb-1">
                        <span className="font-medium text-sm">{country.name}</span>
                        <span className="text-muted-foreground text-sm">
                          {formatNumber(country.value)} ({((country.value / totalVisits) * 100).toFixed(1)}%)
                        </span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-primary"
                          style={{ width: `${(country.value / topCountries[0].value) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}

// Helper to get country flag emoji
function getCountryFlag(countryCode) {
  if (!countryCode || countryCode === 'Unknown') return '🌍';
  try {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  } catch {
    return '🌍';
  }
}